import { Timestamp } from 'firebase/firestore';
import { chain, flatMap, get, isArray, isEmpty, map } from 'lodash';
import { CREDENTIALING_BODIES, isDeprecatedBody, resolveCredentialOn } from 'src/contexts/staffCompetency';
import Time from 'src/mixins/Time';

const LEGACY_TASK_LIST_5TH_ED = 'bacb-bcba-task-list-5th-ed';

export default {
  mixins: [Time],

  data() {
    return {
      // Moved to the Staff Competency context, which owns this vocabulary; kept exposed here so
      // this mixin's consumers are unchanged. Add a body there, not here.
      credentialing: CREDENTIALING_BODIES,
    };
  },

  created() {
    /**
     * Should be defined in created() so that it won't be a reactive object as we don't need that
     */
    /* eslint-disable max-len */
    this.allTaskLists = {
      'bacb-rbt-initial-competency-10-2019': {
        name: 'BACB RBT® Initial Competency Assessment',
        version: '10/2019',
        language: 'en-US',
        groups: {
          measurement: 'Measurement',
          assessment: 'Assessment',
          skills: 'Skill Acquisition and Behavior Reduction',
          professionalism: 'Professionalism and Requirements',
        },
        assessmentTypes: {
          client: 'With a client',
          rolePlay: 'Role-Play',
          interview: 'Interview',
          skipped: 'Skipped',
        },
        tasks: {
          1: {
            label: 'Continuous Measurement',
            group: 'measurement',
            description: 'Implement continuous measurement (e.g., frequency, duration, latency, IRT).',
            assessmentTypes: ['client', 'rolePlay', 'interview'],
          },
          2: {
            label: 'Discontinuous Measurement',
            group: 'measurement',
            description: 'Implement discontinuous measurement procedures (e.g., partial and whole interval, momentary time sampling).',
            assessmentTypes: ['client', 'rolePlay', 'interview'],
          },
          3: {
            label: 'Data and Graphs',
            group: 'measurement',
            description: 'Enter data and update graphs.',
            assessmentTypes: ['client', 'rolePlay', 'interview'],
          },
          4: {
            label: 'Preference Assessments',
            group: 'assessment',
            description: 'Conduct preference assessments.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          5: {
            label: 'ABC Data',
            group: 'assessment',
            description: 'Collect ABC data.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          6: {
            label: 'Discrete-Trial Teaching',
            group: 'skills',
            description: 'Implement discrete-trial teaching procedures.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          7: {
            label: 'Naturalistic Teaching',
            group: 'skills',
            description: 'Implement naturalistic teaching procedures (e.g., incidental teaching).',
            assessmentTypes: ['client', 'rolePlay'],
          },
          8: {
            label: 'Chaining',
            group: 'skills',
            description: 'Implement task analyzed chaining procedures.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          9: {
            label: 'Shaping',
            group: 'skills',
            description: 'Implement shaping procedures.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          10: {
            label: 'Discrimination Training',
            group: 'skills',
            description: 'Implement discrimination training.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          11: {
            label: 'Stimulus Control Transfer',
            group: 'skills',
            description: 'Implement stimulus control transfer procedures.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          12: {
            label: 'Prompting',
            group: 'skills',
            description: 'Implement prompt and prompt fading procedures.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          13: {
            label: 'Token Systems',
            group: 'skills',
            description: 'Implement token systems.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          14: {
            label: 'Crisis/Emergency',
            group: 'skills',
            description: 'Implement crisis/emergency procedures according to protocol.',
            assessmentTypes: ['client', 'rolePlay'],
          },
          15.1: {
            label: 'Antecedent Interventions',
            group: 'skills',
            description: 'Implement interventions based on modification of antecedents such as motivating/establishing operations and discriminative stimuli.',
            assessmentTypes: ['client', 'rolePlay', 'skipped'],
          },
          15.2: {
            label: 'Differential Reinforcement:',
            group: 'skills',
            description: 'Implement differential reinforcement procedures (e.g., DRA, DRO).',
            assessmentTypes: ['client', 'rolePlay', 'skipped'],
          },
          15.3: {
            label: 'Extinction',
            group: 'skills',
            description: 'Implement extinction procedures.',
            assessmentTypes: ['client', 'rolePlay', 'skipped'],
          },
          16: {
            label: 'Session Notes',
            group: 'professionalism',
            description: 'Generate objective session notes by describing what occurred during sessions.',
            assessmentTypes: ['client', 'rolePlay', 'interview'],
          },
          17: {
            label: 'Client Dignity',
            group: 'professionalism',
            description: 'Provide examples of how to maintain client dignity.',
            assessmentTypes: ['interview'],
          },
          18: {
            label: 'Professional Boundaries',
            group: 'professionalism',
            description: 'Provide examples of how to maintain professional boundaries.',
            assessmentTypes: ['interview'],
          },
          19: {
            label: 'Supervision Requirements',
            group: 'professionalism',
            description: 'Describe BACB Supervision Standards for RBTs.',
            assessmentTypes: ['interview'],
          },
          20: {
            label: 'Clinical Direction',
            group: 'professionalism',
            description: 'Describe at least one situation in which you’d seek clinical direction from your supervisor.',
            assessmentTypes: ['interview'],
          },

        },
      },

      [LEGACY_TASK_LIST_5TH_ED]: {
        name: 'BACB BCBA® Task List (5th ed.)',
        version: '2017',
        language: 'en-US',
        groups: {
          underpinnings: 'Philosophical Underpinnings',
          concepts: 'Concepts and Principles',
          measurement: 'Measurement, Data Display, and Interpretation',
          experiment: 'Experimental Design',
          ethics: 'Ethics Code For Behavior Analysts',
          assessment: 'Behavior Assessment',
          behavior: 'Behavior Change Procedure',
          intervention: 'Selecting and Implementing Intervetions',
          supervision: 'Personnel Supervision and Management',
        },
        assessmentTypes: {
          verified: 'Verified by Supervisor',
        },
        tasks: {
          'A-1': {
            label: 'Identify the goals of behavior analysis as a science',
            group: 'underpinnings',
            description: 'Description, Prediction, and Control',
            assessmentTypes: ['verified'],
          },
          'A-2': {
            label: 'Explain the philosophical assumptions underlying the science of behavior analysis',
            group: 'underpinnings',
            description: 'Selectionism, determinism, empiricism, parsimony, pragmatism',
            assessmentTypes: ['verified'],
          },
          'A-3': {
            label: 'Descibe and explain behavior from the persepctive of radical behaviorism',
            group: 'underpinnings',
            assessmentTypes: ['verified'],
          },
          'A-4': {
            label: 'Distinguish among behaviorism, EAB, ABA, and professional practice guided by the science of behavior analysis.',
            group: 'underpinnings',
            description: 'EAB - Experimnental analysis of behavior, ABA - Applied Behavior Analysis',
            assessmentTypes: ['verified'],
          },
          'A-5': {
            label: 'Describe and define the dimensions of applied behavior analysis',
            group: 'underpinnings',
            description: 'Use Baer, Wolf, & Risley (1968) for descriptions of each dimension',
            assessmentTypes: ['verified'],
          },
          'B-1': {
            label: 'Define and provide examples of behavior, response, and response class.',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-2': {
            label: 'Define and provide examples of stimulus and stimulus class',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-3': {
            label: 'Define and provide examples of respondent and operant conditioning',
            group: 'concepts',
            assessmentTypes: ['verified'],

          },
          'B-4': {
            label: 'Define and provide examples of positive and negative reinforcement contingencies',
            group: 'concepts',
            assessmentTypes: ['verified'],

          },
          'B-5': {
            label: 'Define and provide examples of schedules of reinforcement',
            group: 'concepts',
            assessmentTypes: ['verified'],

          },
          'B-6': {
            label: 'Define and provide examples of positive and negative punishment contingencies',
            group: 'concepts',
            assessmentTypes: ['verified'],

          },
          'B-7': {
            label: 'Define and provide examples of automatic and socially mediated contingencies',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-8': {
            label: 'Define and provide examples of unconditioned, conditioned, and generalized reinforcers and punishers',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-9': {
            label: 'Define and provide examples of operant extinction',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-10': {
            label: 'Define and rpovide examples of stimulus control',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-11': {
            label: 'Define and provide examples of discrimiantion, generalization, and maintenance',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-12': {
            label: 'Define and provide examples of motivating operations',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-13': {
            label: 'Define and provide examples of rule-governed and contingency-shaped behavior',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-14': {
            label: 'Define and provide examples of the verbal operants',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'B-15': {
            label: 'Define and provide examples of derived stimulus relations',
            group: 'concepts',
            assessmentTypes: ['verified'],
          },
          'C-1': {
            label: 'Establish operational definitions of behavior',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-2': {
            label: 'Distinguish among direct, indirect, and product measures of behavior',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-3': {
            label: 'Measure occurrence (count, frequency, rate, percentage).',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-4': {
            label: 'Measure temporal dimensions of behavior (duration, latency, interresponse time).',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-5': {
            label: 'Measure form and strength of behavior',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-6': {
            label: 'Measure form and strength of behavior (topography, magnitude).',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-7': {
            label: 'Measure trials to criterion',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-8': {
            label: 'Design and implement sampling procedures (interval recording, time sampling).',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-9': {
            label: 'Evaluate the validity and reliability of measurement procedures',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-10': {
            label: 'Select a measurement system to obtain representative data given the dimensions of behavior and the logistics of observing and recording',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'C-11': {
            label: 'Graph data to communicate relevant quantitative relations (e.g., equal-interval graphs, bar graphs, cumulative records).',
            group: 'measurement',
            assessmentTypes: ['verified'],
          },
          'D-1': {
            label: 'Interpret graphed data',
            group: 'experiment',
            assessmentTypes: ['verified'],
          },
          'D-2': {
            label: 'Distinguish between dependent and independent variables',
            group: 'experiment',
            assessmentTypes: ['verified'],
          },
          'D-3': {
            label: 'Distinguish between internal and external validity',
            group: 'experiment',
            assessmentTypes: ['verified'],
          },
          'D-4': {
            label: 'Identify the defining features of a single-subject experimental design',
            group: 'experiment',
            description: 'Individuals serve as their own controls, repeated measures, prediction, verification, replication',
            assessmentTypes: ['verified'],
          },
          'D-5': {
            label: 'Describe the advantages of single-subject experimental designs compared to group designs',
            group: 'experiment',
            assessmentTypes: ['verified'],
          },
          'D-6': {
            label: 'Use single-subject experimental designs',
            group: 'experiment',
            description: 'reversal, multiple baseline, multielement, changing criterion',
            assessmentTypes: ['verified'],
          },
          'D-7': {
            label: 'Describe rationales for conducting comparative, component, and parametric analyses',
            group: 'experiment',
            assessmentTypes: ['verified'],
          },
          'E-1': {
            label: 'Introduction to Ethics',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'E-2': {
            label: 'Responsibility as a Professional',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'E-3': {
            label: 'Responsibility in Practice',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'E-4': {
            label: 'Responsibility to Clients and Stakeholders',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'E-5': {
            label: 'Responsibility to Supervisees and Trainees',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'E-6': {
            label: 'Responsibility in Public Statements',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'E-7': {
            label: 'Responsibility in Research',
            group: 'ethics',
            assessmentTypes: ['verified'],
          },
          'F-1': {
            label: 'Review records and available data (e.g., educational, medical, historical) at the outset of the case.',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-2': {
            label: 'Determine the need for behavior-analytic services',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-3': {
            label: 'Identify and prioritize socially significant behavior-change goals',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-4': {
            label: 'Conduct assessments of relevant skill strengths and deficits',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-5': {
            label: 'Conduct preference assessments',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-6': {
            label: 'Describe the common functions of problem behavior',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-7': {
            label: 'Conduct a descriptive assessment of problem behavior',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-8': {
            label: 'Conduct a functional analysis of problem behavior',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'F-9': {
            label: 'Interpret functional assessment data',
            group: 'assessment',
            assessmentTypes: ['verified'],
          },
          'G-1': {
            label: 'Use positive and negative reinforcement procedures to strengthen behavior',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-2': {
            label: 'Use interventions based on motivating operations and discriminative stimuli',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-3': {
            label: 'Establish and use conditioned reinforcers',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-4': {
            label: 'Use stimulus and response prompts and fading',
            group: 'behavior',
            description: 'errorless learning, most-to-least, least-to-most, prompt delay, stimulus fading',
            assessmentTypes: ['verified'],
          },
          'G-5': {
            label: 'Use modeluing and imitation training',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-6': {
            label: 'Use instructions and rules',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-7': {
            label: 'Use shaping',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-8': {
            label: 'Use chaining',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-9': {
            label: 'Use discrete-trial, free-operant, and naturalistic teaching arrangements',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-10': {
            label: 'Teach simple and conditional discriminations',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-11': {
            label: 'Use Skinner\'s analysis to teach verbal behavior',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-12': {
            label: 'Use equvalence-based instruction',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-13': {
            label: 'Use the high-probability instructional sequence',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-14': {
            label: 'Use Reinforcement Procedures To Weaken Behavior',
            group: 'behavior',
            description: 'DRA, FCT, DRO, DRL, NCR',
            assessmentTypes: ['verified'],
          },
          'G-15': {
            label: 'Use extinction',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-16': {
            label: 'Use positive and negative punishment (e.g time-out, response cost, overcorrection).',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-17': {
            label: 'Use token economies',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-18': {
            label: 'Use Group Contingencies',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-19': {
            label: 'Use Contingency Contracting',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-20': {
            label: 'Use self-management strategies',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-21': {
            label: 'Use procedures to promote stimulus and response generalization',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'G-22': {
            label: 'Use procedures to promote maintenance',
            group: 'behavior',
            assessmentTypes: ['verified'],
          },
          'H-1': {
            label: 'State intervention goals in observable and measureable terms',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-2': {
            label: 'Identify potential interventions based on assessment results and the best available scientific evidence',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-3': {
            label: 'Recommend interventions goals and strategies based on such factors as client preferences, supporting enviornments, risks, constraints, and social validity',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-4': {
            label: 'When a target behavior is to be decreased, select an acceptable alternative behavior to be established or increased',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-5': {
            label: 'Plan for possible unwanted effects when using reinforcement, extinction, and punishment procedures',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-6': {
            label: 'Monitor client progress and treatment integrity',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-7': {
            label: 'Make data-based decisions about the effectiveness of the intervention and the need for treatment revision',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-8': {
            label: 'Make data-based decisions about the need for ongoing services',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'H-9': {
            label: 'Collaborate with others who support and/or provide services to clients',
            group: 'intervention',
            assessmentTypes: ['verified'],
          },
          'I-1': {
            label: 'State the reasons for using behavior-analytic supervision and the potential risks of ineffective supervision.',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          '1-2': {
            label: 'Establish clear performance expectations for the supervisor and supervisee',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          'I-3': {
            label: 'Select supervision goals based on an assessment of the supervisee’s skills',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          'I-4': {
            label: 'Train personnel to competently perform assessment and intervention procedures',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          'I-5': {
            label: 'Use performance monitoring, feedback, and reinforcement systems',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          'I-6': {
            label: 'Use a functional assessment approach to identify variables affecting personnel performance',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          'I-7': {
            label: 'Use function-based strategies to improve personnel performance',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
          'I-8': {
            label: 'Evaluate the effects of supervision (e.g., on client outcomes, on supervisee repertoires',
            group: 'supervision',
            assessmentTypes: ['verified'],
          },
        },
      },
      'bacb-bcba-task-list-6th-ed': {
        name: 'BACB BCBA® Test Content Area (6th ed.)',
        version: '2025',
        language: 'en-US',
        groups: {
          underpinnings: 'Behaviorism and Philosophical Foundations',
          concepts: 'Concepts and Principles',
          measurement: 'Measurement, Data Display, and Interpretation',
          experiment: 'Experimental Design',
          ethics: 'Ethical and Professional Issues',
          assessment: 'Behavior Assessment',
          behavior: 'Behavior-Change Procedure',
          intervention: 'Selecting and Implementing Interventions',
          supervision: 'Personnel Supervision and Management',
        },
        assessmentTypes: {
          verified: 'Verified by Supervisor',
        },
        tasks: {
          'A-1': {
            label: 'Identify the goals of behavior analysis as a science (i.e., description, prediction, control)',
            group: 'underpinnings',
            assessmentTypes: [
              'verified',
            ],
          },
          'A-2': {
            label: 'Explain the philosophical assumptions underlying the science of behavior analysis (e.g., selectionism, determinism, empiricism, parsimony, pragmatism).',
            group: 'underpinnings',
            assessmentTypes: [
              'verified',
            ],
          },
          'A-3': {
            label: 'Explain behavior from the perspective of radical behaviorism',
            group: 'underpinnings',
            assessmentTypes: [
              'verified',
            ],
          },
          'A-4': {
            label: 'Distinguish among behaviorism, the experimental analysis of behavior, applied behavior analysis, and professional practice guided by the science of behavior analysis',
            group: 'underpinnings',
            assessmentTypes: [
              'verified',
            ],
          },
          'A-5': {
            label: 'Identify and describe dimensions of applied behavior analysis',
            group: 'underpinnings',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-1': {
            label: 'Identify and distinguish among behavior, response, and response class',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-2': {
            label: 'Identify and distinguish between stimulus and stimulus class',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-3': {
            label: 'Identify and distinguish between respondent and operant conditioning',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-4': {
            label: 'Identify and distinguish between positive and negative reinforcement contingencies',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-5': {
            label: 'Identify and distinguish between positive and negative punishment contingencies',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-6': {
            label: 'Identify and distinguish between automatic and socially mediated contingencies',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-7': {
            label: 'Identify and distinguish among unconditioned, conditioned, and generalized reinforcers',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-8': {
            label: 'Identify and distinguish among unconditioned, conditioned, and generalized punishers',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-9': {
            label: 'Identify and distinguish among simple schedules of reinforcement',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-10': {
            label: 'Identify and distinguish among concurrent, multiple, mixed, and chained schedules of reinforcement',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-11': {
            label: 'Identify and distinguish between operant and respondent extinction as operations and processes',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-12': {
            label: 'Identify examples of stimulus control',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-13': {
            label: 'Identify examples of stimulus discrimination',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-14': {
            label: 'Identify and distinguish between stimulus and response generalization',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-15': {
            label: 'Identify examples of response maintenance',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-16': {
            label: 'Identify examples of motivating operations',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-17': {
            label: 'Distinguish between motivating operations and stimulus control',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-18': {
            label: 'Identify and distinguish between rule-governed and contingency-shaped behavior',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-19': {
            label: 'Identify and distinguish among verbal operants',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-20': {
            label: 'Identify the role of multiple control in verbal behavior',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-21': {
            label: 'Identify examples of processes that promote emergent relations and generative performance',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-22': {
            label: 'Identify ways behavioral momentum can be used to understand response persistence',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-23': {
            label: 'Identify ways the matching law can be used to interpret response allocation',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'B-24': {
            label: ' Identify and distinguish between imitation and observational learning',
            group: 'concepts',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-1': {
            label: 'Create operational definitions of behavior',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-2': {
            label: 'Distinguish among direct, indirect, and product measures of behavior',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-3': {
            label: 'Measure occurrence',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-4': {
            label: 'Measure temporal dimensions of behavior (duration, latency, interresponse time)',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-5': {
            label: 'Distinguish between continuous and discontinuous measurement procedures',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-6': {
            label: 'Design and apply discontinuous measurement procedures (e.g., interval recording, time sampling)',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-7': {
            label: 'Measure efficiency (e.g., trials to criterion, cost-benefit analysis, training duration)',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-8': {
            label: 'Evaluate the validity and reliability of measurement procedures',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-9': {
            label: 'Select a measurement procedure to obtain representative data that accounts for the critical dimension of the behavior and environmental constraints',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-10': {
            label: 'Graph data to communicate relevant quantitative relations (e.g., equal-interval graphs, bar graphs, cumulative records)',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-11': {
            label: 'Interpret graphed data',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'C-12': {
            label: 'Select a measurement procedure to obtain representative procedural integrity data that accounts for relevant dimensions (e.g., accuracy, dosage) and environmental constraints',
            group: 'measurement',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-1': {
            label: 'Distinguish between dependent and independent variables',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-2': {
            label: 'Distinguish between internal and external validity',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-3': {
            label: 'Identify threats to internal validity (e.g., history, maturation)',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-4': {
            label: 'Identify the defining features of a single-subject experimental design (e.g., individuals serve as their own controls, repeated measures, prediction, verification, replication)',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-5': {
            label: 'Identify the relative strengths of single-case experimental designs and group designs',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-6': {
            label: 'Critique and interpret data from single-case experimental designs',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-7': {
            label: 'Distinguish among reversal, multiple-baseline, multielement, and changing-criterion designs',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-8': {
            label: 'Identify rationales for conducting comparative, component, and parametric analyses',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'D-9': {
            label: 'Apply single-case experimental designs',
            group: 'experiment',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-1': {
            label: 'Identify and apply core principles underlying the ethics codes for BACB certificants (e.g., benefit others; treat others with compassion, dignity, and respect; behave with integrity)',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-2': {
            label: 'Identify the risks to oneself, others, and the profession as a result of engaging in unethical behavior',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-3': {
            label: 'Develop and maintain competence by engaging in professional development activities (e.g., read literature, seek consultation, establish mentors)',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-4': {
            label: 'Identify and comply with requirements for collecting, using, protecting, and disclosing confidential information',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-5': {
            label: 'Identify and comply with requirements for making public statements about professional activities (e.g., social media activity; misrepresentation of professional credentials, behavior analysis, and service outcomes)',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-6': {
            label: 'Identify the conditions under which services or supervision should be discontinued and apply steps that should be taken when transitioning clients and supervisees to another professional',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-7': {
            label: 'Identify types of and risks associated with multiple relationships, and how to mitigate those risks when they are unavoidable',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-8': {
            label: 'Identify and apply interpersonal and other skills (e.g., accepting feedback, listening actively, seeking input, collaborating) to establish and maintain professional relationships',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-9': {
            label: 'Engage in cultural humility in service delivery and professional relationships',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-10': {
            label: 'Apply culturally responsive and inclusive service and supervision activities',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-11': {
            label: 'Identify personal biases and how they might interfere with professional activity',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'E-12': {
            label: 'Identify and apply the legal, regulatory, and practice requirements (e.g., licensure, jurisprudence, funding, certification) relevant to the delivery of behavior-analytic services',
            group: 'ethics',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-1': {
            label: 'Identify relevant sources of information in records (e.g., educational, medical, historical) at the outset of the case',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-2': {
            label: 'Identify and integrate relevant cultural variables in the assessment process',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-3': {
            label: 'Design and evaluate assessments of relevant skill strengths and areas of need',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-4': {
            label: 'Design and evaluate preference assessments',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-5': {
            label: 'Design and evaluate descriptive assessments',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-6': {
            label: 'Design and evaluate functional analyses',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-7': {
            label: 'Interpret assessment data to determine the need for behavior-analytic services and/or referral to others',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'F-8': {
            label: 'Interpret assessment data to identify and prioritize socially significant, client-informed, and culturally responsive behavior-change procedures and goals',
            group: 'assessment',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-1': {
            label: 'Design and evaluate positive and negative reinforcement procedures',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-2': {
            label: 'Design and evaluate differential reinforcement (e.g., DRA, DRO, DRL, DRH) procedures with and without extinction',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-3': {
            label: 'Design and evaluate time-based reinforcement (e.g., fixed-time) schedules',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-4': {
            label: 'Identify procedures to establish and use conditioned reinforcers (e.g., token economies)',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-5': {
            label: 'Incorporate motivating operations and discriminative stimuli into behavior-change procedures',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-6': {
            label: 'Design and evaluate procedures to produce simple and conditional discriminations',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-7': {
            label: 'Select and evaluate stimulus and response prompting procedures (e.g., errorless, most-to-least, least-to-most)',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-8': {
            label: 'Design and implement procedures to fade stimulus and response prompts (e.g., prompt delay, stimulus fading)',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-9': {
            label: 'Design and evaluate modeling procedures',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-10': {
            label: 'Design and evaluate instructions and rules',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-11': {
            label: 'Shape dimensions of behavior',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-12': {
            label: 'Select and implement chaining procedures',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-13': {
            label: 'Design and evaluate trial-based and free-operant procedures',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-14': {
            label: 'Design and evaluate group contingencies',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-15': {
            label: 'Design and evaluate procedures to promote stimulus and response generalization',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-16': {
            label: 'Design and evaluate procedures to maintain desired behavior change following intervention (e.g., schedule thinning, transferring to naturally occurring reinforcers)',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-17': {
            label: 'Design and evaluate positive and negative punishment (e.g., time-out, response cost, overcorrection)',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-18': {
            label: 'Evaluate emotional and elicited effects of behavior-change procedures',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'G-19': {
            label: 'Design and evaluate procedures to promote emergent relations and generative performance',
            group: 'behavior',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-1': {
            label: 'Develop intervention goals in observable and measurable terms',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-2': {
            label: 'Identify and recommend interventions based on assessment results, scientific evidence, client preferences, and contextual fit (e.g., expertise required for implementation, cultural variables, environmental resources)',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-3': {
            label: 'Select socially valid alternative behavior to be established or increased when a target behavior is to be decreased',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-4': {
            label: 'Plan for and attempt to mitigate possible unwanted effects when using reinforcement, extinction, and punishment procedures',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-5': {
            label: 'Plan for and attempt to mitigate possible relapse of the target behavior',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-6': {
            label: 'Make data-based decisions about procedural integrity',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-7': {
            label: 'Make data-based decisions about the effectiveness of the intervention and the need for modification',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'H-8': {
            label: 'Collaborate with others to support and enhance client services',
            group: 'intervention',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-1': {
            label: 'Identify the benefits of using behavior-analytic supervision (e.g., improved client outcomes, improved staff performance and retention)',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-2': {
            label: 'Identify and apply strategies for establishing effective supervisory relationships (e.g., executing supervisor-supervisee contracts, establishing clear expectations, giving and accepting feedback)',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-3': {
            label: 'Identify and implement methods that promote equity in supervision practices',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-4': {
            label: 'Select supervision goals based on an assessment of the supervisee’s skills, cultural variables, and the environment',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-5': {
            label: 'Identify and apply empirically validated and culturally responsive performance management procedures (e.g., modeling, practice, feedback, reinforcement, task clarification, manipulation of response effort)',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-6': {
            label: 'Apply a function-based approach (e.g., performance diagnostics) to assess and improve supervisee behavior',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
          'I-7': {
            label: 'Make data-based decisions about the efficacy of supervisory practices',
            group: 'supervision',
            assessmentTypes: [
              'verified',
            ],
          },
        },
      },
    };
    /* eslint-enable max-len */
  },

  computed: {

    credentialingStaffMemberColumns() {
      /**
       * The credential to read for a body, resolved as of today.
       *
       * A body used to hold at most one credential, which is why taking the first match was safe. It
       * can now hold several, because a renewal is a new record carrying its own window — so the
       * first match is the OLDEST, and these columns would show an expired certificate's number and
       * expiry beside a person who renewed years ago.
       *
       * The rule itself is published by Staff Competency and shared with the tokens printed on notes
       * and reports, because the two had it written twice and drifted at the first edit.
       */
      const getStaffMemberCredentialProp = (staffMember, credentialId, prop) => {
        const asOf = this.todayCalendarDate();
        const forBody = (staffMember?.credentials ?? [])
          .filter((credential) => credential?.organization === credentialId);

        return resolveCredentialOn(forBody, asOf)?.[prop] ?? null;
      };

      return flatMap(
        this.credentialing,
        (credential, credentialId) => {
          const columns = [
            {
              credentialId,
              name: credentialId,
              align: 'left',
              field: 'certificateId',
              format: (val, staffMember) => (
                getStaffMemberCredentialProp(staffMember, credentialId, 'certificateId')
              ),
              label: credential.certificationLabel
                ? `${credential.name}: ${credential.certificationLabel}`
                : `${credential.name}: ${this.$t('staff.credential.inputCertificateIdLabel')}`,
            },
          ];
          if (!isEmpty(credential.levels)) {
            columns.push({
              credentialId,
              name: `${credentialId}_level`,
              align: 'left',
              field: 'level',
              format: (val, staffMember) => (
                getStaffMemberCredentialProp(staffMember, credentialId, 'level')
              ),
              label: `${credential.name}: ${this.$t('staff.credential.inputLevelLabel')}`,
            });
          }

          if (credential.hasTaxonomy) {
            columns.push({
              credentialId,
              name: `${credentialId}_taxonomy`,
              align: 'left',
              field: 'taxonomy',
              format: (val, staffMember) => (
                getStaffMemberCredentialProp(staffMember, credentialId, 'taxonomy')
              ),
              label: `${credential.name}: ${this.$t('staff.credential.inputTaxonomyLabel')}`,
            });
          }
          if (credential.hasExpirationDate) {
            columns.push({
              credentialId,
              name: `${credentialId}_expirationDate`,
              align: 'left',
              field: 'expirationDate',
              format: (val, staffMember) => {
                const expirationDate = getStaffMemberCredentialProp(staffMember, credentialId, 'expirationDate');
                return expirationDate instanceof Timestamp ? this.getLocaleDateString(expirationDate) : expirationDate;
              },
              label: `${credential.name}: ${this.$t('staff.credential.inputExpirationDateLabel')}`,
            });
          }
          return columns;
        },
      );
    }, // credentialingColumns()

    /**
     * The credential columns with the deprecated bodies' columns at the end.
     *
     * A deprecated body still generates its columns, because a saved view can name one and whatever
     * a clinic recorded under it has to stay visible. What it should not do is sit in the middle of
     * the picker between two bodies people actually use.
     *
     * Order is otherwise left exactly as the bodies produce it: this sorts the deprecated ones out
     * of the way and rearranges nothing else.
     */
    credentialingStaffMemberColumnsOrdered() {
      const columns = this.credentialingStaffMemberColumns;

      return [
        ...columns.filter((column) => !isDeprecatedBody(column.credentialId)),
        ...columns.filter((column) => isDeprecatedBody(column.credentialId)),
      ];
    }, // credentialingStaffMemberColumnsOrdered()

    credentialOrganizations() {
      return map(
        this.credentialing,
        (credentialingInfo, value) => ({ value, label: credentialingInfo.name }),
      );
    }, // credentialOrganizations()

    credentialStatuses() {
      return map(this.$tm('staff.credential.statuses'), (label, value) => ({ value, label }));
    }, // credentialStatuses()

    credentialTaskLists() {
      return map(
        this.allTaskLists,
        (taskListInfo, value) => ({ value, label: taskListInfo.name }),
      );
    }, // credentialTaskLists()

    taskListStatuses() {
      return map(this.$tm('staff.taskList.statuses'), (label, value) => ({ value, label }));
    }, // taskListStatuses()

  },

  methods: {

    getTaskListGroups(taskList, filter = null) {
      if (filter === null) {
        return get(this.allTaskLists, [taskList, 'groups'], []);
      }
      const filteredGroups = chain(this.allTaskLists)
        .get([taskList, 'tasks'], [])
        .omitBy((task, taskId) => {
          return (isArray(filter) && (!filter.includes(taskId)));
        })
        .map('group')
        .defaultTo([])
        .value();

      return chain(this.allTaskLists)
        .get([taskList, 'groups'], {})
        .omitBy((group, groupId) => !filteredGroups.includes(groupId))
        .value();
    }, // getTaskListGroups()

    getTaskListTasks(taskList, group = null, filter = null) {
      if (group === null) {
        return chain(this.allTaskLists)
          .get([taskList, 'tasks'], [])
          .omitBy((task, taskId) => (isArray(filter) && (!filter.includes(taskId))))
          .value();
      }
      return chain(this.allTaskLists)
        .get([taskList, 'tasks'], {})
        .omitBy((task) => task.group !== group)
        .omitBy((task, taskId) => (isArray(filter) && (!filter.includes(taskId))))
        .value();
    }, // getTaskListTasks()

    getTaskListName(taskListId) {
      return get(this.allTaskLists, [taskListId, 'name'], null);
    },

    getFilteredTaskLists(currentValue) {
      return this.credentialTaskLists.filter((option) => option.value !== LEGACY_TASK_LIST_5TH_ED
        || option.value === currentValue);
    },

  },
};
